import { FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent } from '@mui/material'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { API_BASE_URL } from '../config'
import { useAuth } from '../contexts/AuthContext'

interface AssistantSelectorProps {
  selectedAssistant: string | null
  onAssistantChange: (assistantId: string, assistantName: string) => void
}

export const AssistantSelector: React.FC<AssistantSelectorProps> = ({
  selectedAssistant,
  onAssistantChange,
}) => {
  const [assistants, setAssistants] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const { token } = useAuth()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const fetchAssistants = async () => {
      if (!token) {
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`${API_BASE_URL}/assistants`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        
        // Handle wrapped serverless-http response
        let assistantsData
        if (data.body && typeof data.body === 'string') {
          // Response is wrapped by serverless-http
          const parsedBody = JSON.parse(data.body)
          assistantsData = parsedBody.assistants
        } else if (data.assistants) {
          // Direct response from Express
          assistantsData = data.assistants
        } else {
          throw new Error('Unexpected response format')
        }
        
        // Convert array format to Record<name, id> format
        const assistantsRecord: Record<string, string> = {}
        assistantsData.forEach((assistant: { name: string; id: string }) => {
          assistantsRecord[assistant.name] = assistant.id
        })
        setAssistants(assistantsRecord)

        // Check URL parameter for assistant selection
        const urlAssistant = searchParams.get('assistant')
        let selectedAssistantFromUrl: string | null = null

        if (urlAssistant) {
          // Find assistant name that matches URL parameter (convert back from slug)
          const urlAssistantName = Object.keys(assistantsRecord).find(
            (name) => name.toLowerCase().replace(/\s+/g, '-') === urlAssistant
          )
          if (urlAssistantName) {
            selectedAssistantFromUrl = urlAssistantName
          }
        }

        // Auto-select assistant based on URL parameter or default to first
        if (!selectedAssistant && Object.keys(assistantsRecord).length > 0) {
          const assistantToSelect = selectedAssistantFromUrl || Object.keys(assistantsRecord)[0]
          const assistantId = assistantsRecord[assistantToSelect]
          onAssistantChange(assistantId, assistantToSelect)
        }
      } catch (error) {
        console.error('Failed to fetch assistants:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAssistants()
  }, [token]) // Removed selectedAssistant and onAssistantChange from deps to prevent loops

  const handleChange = (event: SelectChangeEvent) => {
    const selectedName = event.target.value as string
    const selectedId = assistants[selectedName]
    onAssistantChange(selectedId, selectedName)
  }

  const selectedName =
    Object.keys(assistants).find((name) => assistants[name] === selectedAssistant) || ''

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: { xs: 150, sm: 200 },
        maxWidth: { xs: 180, sm: 250 },
      }}
    >
      <InputLabel id="assistant-select-label">Assistant</InputLabel>
      <Select
        labelId="assistant-select-label"
        id="assistant-select"
        value={selectedName}
        label="Assistant"
        onChange={handleChange}
        disabled={loading}
        sx={{
          '& .MuiSelect-select': {
            fontSize: { xs: '0.875rem', sm: '1rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      >
        {Object.keys(assistants).map((name) => (
          <MenuItem
            key={name}
            value={name}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              whiteSpace: 'normal',
            }}
          >
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
